import React, { createContext, useContext, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import ConfirmModal from '../components/common/ConfirmModal';
import AlertModal from '../components/common/AlertModal';

const ModalContext = createContext(null);

export const useModal = () => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error('useModal must be used within a ModalProvider');
  }
  return context;
};

export const ModalProvider = ({ children }) => {
  const [confirmState, setConfirmState] = useState({
    isOpen: false,
    title: '',
    message: '',
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    variant: 'danger',
    onConfirm: () => {},
    onCancel: () => {},
    isLoading: false
  });

  const [alertState, setAlertState] = useState({
    isOpen: false,
    title: '',
    message: '',
    buttonText: 'OK',
    variant: 'info',
    onClose: () => {}
  });

  const showConfirm = useCallback((options) => {
    return new Promise((resolve) => {
      setConfirmState({
        isOpen: true,
        title: options.title || 'Confirm Action',
        message: options.message,
        confirmText: options.confirmText || 'Confirm',
        cancelText: options.cancelText || 'Cancel',
        variant: options.variant || 'danger',
        isLoading: false,
        onConfirm: () => {
          resolve(true);
          setConfirmState(prev => ({ ...prev, isOpen: false }));
        },
        onCancel: () => {
          resolve(false);
          setConfirmState(prev => ({ ...prev, isOpen: false }));
        }
      });
    });
  }, []);

  const showAlert = useCallback((options) => {
    return new Promise((resolve) => {
      setAlertState({
        isOpen: true,
        title: options.title || 'Alert',
        message: options.message,
        buttonText: options.buttonText || 'OK',
        variant: options.variant || 'info',
        onClose: () => {
          resolve();
          setAlertState(prev => ({ ...prev, isOpen: false }));
        }
      });
    });
  }, []);

  const confirmDelete = useCallback((itemName) => {
    return showConfirm({
      title: 'Confirm Delete',
      message: `Delete "${itemName}"? This cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      variant: 'danger'
    });
  }, [showConfirm]);

  const confirmRestore = useCallback((message) => {
    return showConfirm({
      title: 'Confirm Restore',
      message: message || 'Restore this version? This will create a new version based on the selected one.',
      confirmText: 'Restore',
      cancelText: 'Cancel',
      variant: 'warning'
    });
  }, [showConfirm]);

  const confirmDiscard = useCallback((message) => {
    return showConfirm({
      title: 'Confirm Discard',
      message: message || 'Discard current changes? This cannot be undone.',
      confirmText: 'Discard',
      cancelText: 'Keep',
      variant: 'warning'
    });
  }, [showConfirm]);

  const handleConfirmClose = () => {
    confirmState.onCancel();
  };

  const handleAlertClose = () => {
    alertState.onClose();
  };

  const value = {
    showConfirm,
    showAlert,
    confirmDelete,
    confirmRestore,
    confirmDiscard
  };

  return (
    <ModalContext.Provider value={value}>
      {children}
      
      <ConfirmModal
        isOpen={confirmState.isOpen}
        onClose={handleConfirmClose}
        onConfirm={confirmState.onConfirm}
        title={confirmState.title}
        message={confirmState.message}
        confirmText={confirmState.confirmText}
        cancelText={confirmState.cancelText}
        variant={confirmState.variant}
        isLoading={confirmState.isLoading}
      />
      
      <AlertModal
        isOpen={alertState.isOpen}
        onClose={handleAlertClose}
        title={alertState.title}
        message={alertState.message}
        buttonText={alertState.buttonText}
        variant={alertState.variant}
      />
    </ModalContext.Provider>
  );
};

ModalProvider.propTypes = {
  children: PropTypes.node.isRequired
};

